import { paymentLinks } from "../constants/index.tsx";
import type {
  SupportedLanguage,
  PaymentTier,
  PaymentLanguage,
} from "../types/i18n";

/**
 * Maps a supported language to its corresponding payment language.
 * Regional languages (Croatian, Serbian, Bosnian) share Croatian payment pages.
 *
 * @param lang - The user's current language
 * @returns The payment language to use for Stripe checkout
 */
const getPaymentLanguage = (lang: SupportedLanguage): PaymentLanguage => {
  // Croatian, Serbian, and Bosnian use Croatian payment pages
  if (["hr", "sr", "bs"].includes(lang)) return "hr";

  // German uses German payment pages
  if (lang === "de") return "de";

  // English, Slovenian, and any other languages use English payment pages
  return "en";
};

/**
 * Gets the appropriate Stripe payment link based on the payment tier and user's language.
 * Automatically detects the environment (dev/prod) and returns the correct link.
 *
 * @param tier - The payment tier (basic, premium, or exclusive)
 * @param language - The user's current language
 * @returns The Stripe checkout URL
 */
export const getPaymentLink = (
  tier: PaymentTier,
  language: SupportedLanguage,
): string => {
  // Detect environment: production build uses 'prod' links, otherwise 'dev'
  const env = process.env.NODE_ENV === "production" ? "prod" : "dev";

  // Map the user's language to a payment language
  const paymentLang = getPaymentLanguage(language);

  // Get the base Stripe checkout URL
  const baseUrl = paymentLinks[env][paymentLang][tier];

  // Append client_reference_id with language and timestamp for uniqueness
  const clientReferenceId = `${language}_${Date.now()}`;

  // Check if URL already has query parameters (contains ?)
  // German and Croatian links have ?locale=, English doesn't
  const separator = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${separator}client_reference_id=${clientReferenceId}`;
};
